import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { createCategory, updateCategory, deleteCategory } from '../api';
import { Category } from '../types/category';

export const useCategoryOperations = () => {
  const queryClient = useQueryClient();

  const handleCreateCategory = async (name: string, parentId?: number): Promise<void> => {
    try {
      const newCategory = await createCategory(name, parentId);
      queryClient.setQueryData<Category[]>(['categories'], (prev: Category[] | undefined) => 
        prev ? [...prev, newCategory] : [newCategory]
      );
      message.success('Category created successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      return;
    } catch (error) {
      console.error('Error creating category:', error);
      message.error('Failed to create category');
      throw error;
    }
  };

  const handleUpdateCategory = async (categoryId: number, name: string, parentId?: number) => {
    try {
      const updatedCategory = await updateCategory(categoryId, name, parentId);
      queryClient.setQueryData<Category[]>(['categories'], (prev: Category[] | undefined) =>
        prev ? prev.map((cat: Category) => cat.id === updatedCategory.id ? updatedCategory : cat) : []
      );
      message.success('Category updated successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      return;
    } catch (error) {
      console.error('Error updating category:', error);
      message.error('Failed to update category');
      throw error;
    }
  };

  const handleDeleteCategory = async (categoryId: number): Promise<void> => {
    try {
      await deleteCategory(categoryId);
      queryClient.setQueryData<Category[]>(['categories'], (prev: Category[] | undefined) =>
        prev ? prev.filter((cat: Category) => cat.id !== categoryId) : []
      );
      message.success('Category deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    } catch (error) {
      console.error('Error deleting category:', error);
      message.error('Failed to delete category');
      throw error;
    }
  };

  const handleMoveCategory = async (categoryId: number, newParentId: number | null): Promise<void> => {
    try {
      const categories = queryClient.getQueryData<Category[]>(['categories']);
      const category = categories?.find((cat: Category) => cat.id === categoryId);
      if (!category) throw new Error('Category not found');

      const updatedCategory = await updateCategory(categoryId, category.name, newParentId ?? undefined);
      queryClient.setQueryData<Category[]>(['categories'], (prev) =>
        prev ? prev.map(cat => cat.id === updatedCategory.id ? updatedCategory : cat) : []
      );
      message.success('Category moved successfully');
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      return;
    } catch (error) {
      console.error('Error moving category:', error);
      message.error('Failed to move category');
      throw error;
    }
  };

  return {
    handleCreateCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleMoveCategory
  };
};
